import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  confirmResetPassword,
  confirmSignIn,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut as cognitoSignOut,
} from 'aws-amplify/auth';
import {
  ArrowRight,
  BadgeCheck,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import logo from '../logo.png';
import './ConsoleAuthGate.css';

const loginHeroImage = `${process.env.PUBLIC_URL || ''}/assets/landing-hero-car-960.jpg`;

function friendlyAuthError(error) {
  const name = error?.name || '';
  const message = error?.message || '';

  if (name === 'NotAuthorizedException') return 'Email/username or password is incorrect.';
  if (name === 'UserNotFoundException') return 'No console user was found for that email or username.';
  if (name === 'UserAlreadyAuthenticatedException') return '';
  if (name === 'PasswordResetRequiredException') return 'Password reset is required before signing in.';
  if (name === 'CodeMismatchException') return 'The confirmation code is incorrect.';
  if (name === 'ExpiredCodeException') return 'The confirmation code has expired. Request a new code.';
  if (name === 'LimitExceededException') return 'Too many attempts. Please wait a moment and try again.';
  if (name === 'InvalidPasswordException') return message || 'The new password does not meet the password policy.';
  if (name === 'InvalidParameterException') return message || 'Please check the details and try again.';

  return message || 'Authentication failed. Please try again.';
}

function deliveryText(details) {
  if (!details) return '';
  const destination = details.destination ? ` to ${details.destination}` : '';
  const medium = details.deliveryMedium ? details.deliveryMedium.toLowerCase() : 'message';
  return `Code sent by ${medium}${destination}.`;
}

function challengeTitle(step) {
  if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') return 'Set a new password';
  if (step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') return 'Enter SMS code';
  if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') return 'Enter email code';
  if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') return 'Enter authenticator code';
  if (step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') return 'Complete verification';
  if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') return 'Set up authenticator';
  if (step === 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP') return 'Add recovery email';
  if (step === 'CONFIRM_SIGN_IN_WITH_PASSWORD') return 'Confirm password';
  return 'Additional verification';
}

function isCodeChallenge(step) {
  return [
    'CONFIRM_SIGN_IN_WITH_SMS_CODE',
    'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
    'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
    'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
  ].includes(step);
}

function ConsoleAuthGate({ children }) {
  const [auth, setAuth] = useState({ status: 'checking', user: null });
  const [mode, setMode] = useState('signIn');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [attributeValues, setAttributeValues] = useState({});
  const [resetCode, setResetCode] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetDelivery, setResetDelivery] = useState('');

  const normalizedLogin = useMemo(() => login.trim().toLowerCase(), [login]);

  const completeSignedIn = useCallback(async () => {
    const user = await getCurrentUser();
    setAuth({ status: 'signedIn', user });
    setMode('signIn');
    setPassword('');
    setChallenge(null);
    setChallengeAnswer('');
    setNewPassword('');
    setConfirmPassword('');
    setAttributeValues({});
    setError('');
    setNotice('');
  }, []);

  const handleSignInResult = useCallback(
    async (result) => {
      const step = result?.nextStep?.signInStep;

      if (result?.isSignedIn || step === 'DONE') {
        await completeSignedIn();
        return;
      }

      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setChallenge({
          type: 'newPassword',
          step,
          missingAttributes: result?.nextStep?.missingAttributes || [],
        });
        setMode('challenge');
        return;
      }

      if (isCodeChallenge(step) || step === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        setChallenge({
          type: 'code',
          step,
          details: result?.nextStep?.codeDeliveryDetails,
          additionalInfo: result?.nextStep?.additionalInfo,
        });
        setMode('challenge');
        return;
      }

      if (
        step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' ||
        step === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION' ||
        step === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION'
      ) {
        setChallenge({
          type: 'choice',
          step,
          choices: result?.nextStep?.allowedMFATypes || result?.nextStep?.availableChallenges || [],
        });
        setMode('challenge');
        return;
      }

      if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        setChallenge({
          type: 'totpSetup',
          step,
          details: result?.nextStep?.totpSetupDetails,
        });
        setMode('challenge');
        return;
      }

      if (step === 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP') {
        setChallenge({ type: 'emailSetup', step });
        setMode('challenge');
        return;
      }

      if (step === 'RESET_PASSWORD') {
        setMode('forgotRequest');
        setNotice('Reset your password to continue.');
        return;
      }

      if (step === 'CONFIRM_SIGN_UP') {
        setError('This account is not confirmed yet. Ask an administrator to complete setup.');
        return;
      }

      setError('This account needs an unsupported Cognito sign-in step. Contact an administrator.');
    },
    [completeSignedIn]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const user = await getCurrentUser();
        if (!cancelled) setAuth({ status: 'signedIn', user });
      } catch {
        if (!cancelled) setAuth({ status: 'signedOut', user: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function onSignIn(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!normalizedLogin || !password) {
      setError('Enter your email or username and password.');
      return;
    }

    setBusy(true);
    try {
      const result = await signIn({
        username: normalizedLogin,
        password,
      });
      await handleSignInResult(result);
    } catch (err) {
      if (err?.name === 'UserAlreadyAuthenticatedException') {
        await completeSignedIn();
      } else {
        setError(friendlyAuthError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onChallengeSubmit(event) {
    event.preventDefault();
    if (!challenge) return;

    setError('');
    setNotice('');

    let response = challengeAnswer.trim();
    let options = undefined;

    if (challenge.type === 'newPassword') {
      if (!newPassword || newPassword !== confirmPassword) {
        setError('New passwords must match.');
        return;
      }

      response = newPassword;

      const requiredAttributes = {};
      for (const attribute of challenge.missingAttributes || []) {
        const value = (attributeValues[attribute] || '').trim();
        if (!value) {
          setError(`Enter ${attribute}.`);
          return;
        }
        requiredAttributes[attribute] = value;
      }

      if (Object.keys(requiredAttributes).length) {
        options = { userAttributes: requiredAttributes };
      }
    }

    if (challenge.type === 'emailSetup') {
      if (!response) {
        setError('Enter the email address.');
        return;
      }
    }

    if ((challenge.type === 'code' || challenge.type === 'totpSetup') && !response) {
      setError('Enter the verification code.');
      return;
    }

    setBusy(true);
    try {
      const result = await confirmSignIn({
        challengeResponse: response,
        ...(options ? { options } : {}),
      });
      setChallengeAnswer('');
      await handleSignInResult(result);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onChoice(choice) {
    setError('');
    setBusy(true);
    try {
      const result = await confirmSignIn({ challengeResponse: choice });
      await handleSignInResult(result);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResetRequest(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!normalizedLogin) {
      setError('Enter your email or username first.');
      return;
    }

    setBusy(true);
    try {
      const result = await resetPassword({ username: normalizedLogin });
      setResetDelivery(deliveryText(result?.nextStep?.codeDeliveryDetails));
      setMode('forgotConfirm');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResetConfirm(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!resetCode.trim() || !resetPasswordValue || resetPasswordValue !== resetPasswordConfirm) {
      setError('Enter the code and matching new passwords.');
      return;
    }

    setBusy(true);
    try {
      await confirmResetPassword({
        username: normalizedLogin,
        confirmationCode: resetCode.trim(),
        newPassword: resetPasswordValue,
      });
      setPassword('');
      setResetCode('');
      setResetPasswordValue('');
      setResetPasswordConfirm('');
      setMode('signIn');
      setNotice('Password updated. Sign in with your new password.');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  const signOut = useCallback(async () => {
    await cognitoSignOut();
    setAuth({ status: 'signedOut', user: null });
    setPassword('');
    setMode('signIn');
  }, []);

  if (auth.status === 'signedIn') {
    return children({ signOut, user: auth.user });
  }

  const heading =
    mode === 'forgotRequest'
      ? 'Reset password'
      : mode === 'forgotConfirm'
        ? 'Create new password'
        : mode === 'challenge'
          ? challengeTitle(challenge?.step)
          : 'Sign in';

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="auth-heading">
        <div className="auth-form-side">
          <div className="auth-brand">
            <img src={logo} alt="Haste" />
            <span>Console</span>
          </div>

          <div className="auth-copy">
            <div className="auth-badge">
              <ShieldCheck size={14} />
              Internal access
            </div>
            <h1 id="auth-heading">{auth.status === 'checking' ? 'Checking session' : heading}</h1>
          </div>

          {auth.status === 'checking' ? (
            <div className="auth-loading">
              <Loader2 size={18} className="auth-spin" />
              Checking your current session
            </div>
          ) : null}

          {auth.status !== 'checking' && mode === 'signIn' ? (
            <form className="auth-form" onSubmit={onSignIn}>
              <label className="auth-field">
                <span>Email or username</span>
                <span className="auth-input-wrap">
                  <Mail size={17} />
                  <input
                    type="text"
                    autoComplete="username"
                    value={login}
                    onChange={(event) => setLogin(event.target.value)}
                    placeholder="you@example.com or username"
                  />
                </span>
              </label>

              <label className="auth-field">
                <span>Password</span>
                <span className="auth-input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Password"
                  />
                  <button
                    className="auth-icon-btn"
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </span>
              </label>

              <button className="auth-primary" type="submit" disabled={busy}>
                {busy ? <Loader2 size={17} className="auth-spin" /> : <ArrowRight size={17} />}
                Sign in
              </button>

              <button
                className="auth-link-btn"
                type="button"
                onClick={() => {
                  setError('');
                  setNotice('');
                  setMode('forgotRequest');
                }}
              >
                Forgot password?
              </button>
            </form>
          ) : null}

          {auth.status !== 'checking' && mode === 'challenge' && challenge ? (
            <form className="auth-form" onSubmit={onChallengeSubmit}>
              {challenge.details ? <div className="auth-note">{deliveryText(challenge.details)}</div> : null}

              {challenge.type === 'choice' ? (
                <div className="auth-choice-list">
                  {(challenge.choices || []).map((choice) => (
                    <button
                      key={choice}
                      className="auth-secondary"
                      type="button"
                      disabled={busy}
                      onClick={() => onChoice(choice)}
                    >
                      <BadgeCheck size={16} />
                      {String(choice).replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              ) : null}

              {challenge.type === 'newPassword' ? (
                <>
                  <label className="auth-field">
                    <span>New password</span>
                    <span className="auth-input-wrap">
                      <KeyRound size={17} />
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        placeholder="New password"
                      />
                    </span>
                  </label>
                  <label className="auth-field">
                    <span>Confirm password</span>
                    <span className="auth-input-wrap">
                      <KeyRound size={17} />
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        placeholder="Confirm password"
                      />
                    </span>
                  </label>
                  {(challenge.missingAttributes || []).map((attribute) => (
                    <label className="auth-field" key={attribute}>
                      <span>{attribute}</span>
                      <span className="auth-input-wrap">
                        <Mail size={17} />
                        <input
                          value={attributeValues[attribute] || ''}
                          onChange={(event) =>
                            setAttributeValues((current) => ({
                              ...current,
                              [attribute]: event.target.value,
                            }))
                          }
                          placeholder={attribute}
                        />
                      </span>
                    </label>
                  ))}
                </>
              ) : null}

              {challenge.type === 'totpSetup' ? (
                <>
                  {challenge.details?.sharedSecret ? (
                    <div className="auth-note auth-secret">{challenge.details.sharedSecret}</div>
                  ) : null}
                  <label className="auth-field">
                    <span>Authenticator code</span>
                    <span className="auth-input-wrap">
                      <KeyRound size={17} />
                      <input
                        value={challengeAnswer}
                        onChange={(event) => setChallengeAnswer(event.target.value)}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                      />
                    </span>
                  </label>
                </>
              ) : null}

              {challenge.type === 'emailSetup' ? (
                <label className="auth-field">
                  <span>Email address</span>
                  <span className="auth-input-wrap">
                    <Mail size={17} />
                    <input
                      type="email"
                      value={challengeAnswer}
                      onChange={(event) => setChallengeAnswer(event.target.value)}
                      placeholder="you@example.com"
                    />
                  </span>
                </label>
              ) : null}

              {challenge.type === 'code' ? (
                <label className="auth-field">
                  <span>{challenge.step === 'CONFIRM_SIGN_IN_WITH_PASSWORD' ? 'Password' : 'Verification code'}</span>
                  <span className="auth-input-wrap">
                    <KeyRound size={17} />
                    <input
                      type={challenge.step === 'CONFIRM_SIGN_IN_WITH_PASSWORD' ? 'password' : 'text'}
                      autoComplete={challenge.step === 'CONFIRM_SIGN_IN_WITH_PASSWORD' ? 'current-password' : 'one-time-code'}
                      value={challengeAnswer}
                      onChange={(event) => setChallengeAnswer(event.target.value)}
                      placeholder={challenge.step === 'CONFIRM_SIGN_IN_WITH_PASSWORD' ? 'Password' : '000000'}
                    />
                  </span>
                </label>
              ) : null}

              {challenge.type !== 'choice' ? (
                <button className="auth-primary" type="submit" disabled={busy}>
                  {busy ? <Loader2 size={17} className="auth-spin" /> : <ArrowRight size={17} />}
                  Continue
                </button>
              ) : null}

              <button
                className="auth-link-btn"
                type="button"
                onClick={() => {
                  setMode('signIn');
                  setChallenge(null);
                  setChallengeAnswer('');
                  setError('');
                }}
              >
                Back to sign in
              </button>
            </form>
          ) : null}

          {auth.status !== 'checking' && mode === 'forgotRequest' ? (
            <form className="auth-form" onSubmit={onResetRequest}>
              <label className="auth-field">
                <span>Email or username</span>
                <span className="auth-input-wrap">
                  <Mail size={17} />
                  <input
                    type="text"
                    autoComplete="username"
                    value={login}
                    onChange={(event) => setLogin(event.target.value)}
                    placeholder="you@example.com or username"
                  />
                </span>
              </label>
              <button className="auth-primary" type="submit" disabled={busy}>
                {busy ? <Loader2 size={17} className="auth-spin" /> : <ArrowRight size={17} />}
                Send reset code
              </button>
              <button className="auth-link-btn" type="button" onClick={() => setMode('signIn')}>
                Back to sign in
              </button>
            </form>
          ) : null}

          {auth.status !== 'checking' && mode === 'forgotConfirm' ? (
            <form className="auth-form" onSubmit={onResetConfirm}>
              {resetDelivery ? <div className="auth-note">{resetDelivery}</div> : null}
              <label className="auth-field">
                <span>Confirmation code</span>
                <span className="auth-input-wrap">
                  <KeyRound size={17} />
                  <input
                    value={resetCode}
                    onChange={(event) => setResetCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                  />
                </span>
              </label>
              <label className="auth-field">
                <span>New password</span>
                <span className="auth-input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={resetPasswordValue}
                    onChange={(event) => setResetPasswordValue(event.target.value)}
                    placeholder="New password"
                  />
                </span>
              </label>
              <label className="auth-field">
                <span>Confirm password</span>
                <span className="auth-input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={resetPasswordConfirm}
                    onChange={(event) => setResetPasswordConfirm(event.target.value)}
                    placeholder="Confirm password"
                  />
                </span>
              </label>
              <button className="auth-primary" type="submit" disabled={busy}>
                {busy ? <Loader2 size={17} className="auth-spin" /> : <ArrowRight size={17} />}
                Update password
              </button>
              <button className="auth-link-btn" type="button" onClick={onResetRequest}>
                Send a new code
              </button>
            </form>
          ) : null}

          {error ? <div className="auth-alert auth-alert-error">{error}</div> : null}
          {notice ? <div className="auth-alert auth-alert-success">{notice}</div> : null}
        </div>

        <div
          className="auth-visual"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(7, 10, 18, 0.12), rgba(7, 10, 18, 0.68)), linear-gradient(90deg, rgba(7, 10, 18, 0.32), rgba(7, 10, 18, 0.08)), url("${loginHeroImage}")`,
          }}
          aria-hidden="true"
        >
          <div className="auth-visual-stat">
            <BadgeCheck size={18} />
            Secure staff access
          </div>
        </div>
      </section>
    </main>
  );
}

export default ConsoleAuthGate;
